// src/components/common/DeleteConfirmationModal.js
import React from 'react';
import ReactDOM from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { AlertTriangle, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const DeleteConfirmationModal = ({
    isOpen,
    onClose,
    onConfirm,
    itemName,
    itemType = 'item', // 'businessValue' or 'supportAsset'
    title,
    message,
    confirmText,
    cancelText
}) => {
    const { t } = useTranslation();

    // Default values based on item type
    const defaultTitle = itemType === 'businessValue'
        ? t('businessValues.confirmations.deleteValueTitle', 'Supprimer la valeur métier')
        : itemType === 'unlinkSupportAsset'
        ? t('businessValues.confirmations.unlinkAssetTitle', 'Délier le bien support')
        : itemType === 'ecosystemMeasure'
        ? t('workshop3.activity4.confirmations.deleteMeasureTitle', 'Supprimer la mesure de sécurité')
        : itemType === 'bulkMeasures'
        ? t('workshop3.activity4.confirmations.deleteMeasuresTitle', 'Supprimer les mesures sélectionnées')
        : itemType === 'securityRule'
        ? t('securityFoundation.title', 'Socle de Sécurité')
        : t('businessValues.confirmations.deleteAssetTitle', 'Supprimer le bien support');

    const defaultMessage = itemType === 'businessValue'
        ? t('businessValues.confirmations.deleteValueMessage', { name: itemName })
        : itemType === 'unlinkSupportAsset'
        ? t('businessValues.confirmations.unlinkAssetMessage', { name: itemName }) || `Êtes-vous sûr de vouloir délier ce bien support de cette valeur métier ?`
        : itemType === 'ecosystemMeasure'
        ? t('workshop3.activity4.confirmations.deleteMeasureMessage', { name: itemName }) || `Êtes-vous sûr de vouloir supprimer définitivement cette mesure de sécurité ?`
        : itemType === 'bulkMeasures'
        ? t('workshop3.activity4.confirmations.deleteMeasuresMessage', { count: itemName }) || `Êtes-vous sûr de vouloir supprimer définitivement ces ${itemName} mesures ?`
        : itemType === 'securityRule'
        ? t('securityFoundation.status.confirmDeleteRule', { name: itemName }) || `Êtes-vous sûr de vouloir supprimer la règle "${itemName}" ?`
        : t('businessValues.confirmations.deleteAssetMessage', { name: itemName });

    const defaultConfirmText = itemType === 'unlinkSupportAsset'
        ? t('common.unlink', 'Délier')
        : t('common.delete', 'Supprimer');
    const defaultCancelText = t('common.cancel', 'Annuler');

    if (!isOpen) return null;

    const getCascadeWarningMessage = () => {
        if (itemType === 'businessValue') {
            return "Attention : La suppression de cette valeur métier supprimera également tous les événements redoutés, scénarios stratégiques, chemins d'attaque et scénarios opérationnels qui y sont liés.";
        } else if (itemType === 'dreadedEvent') {
            return "Attention : La suppression de cet événement redouté supprimera également les scénarios stratégiques, chemins d'attaque et scénarios opérationnels qui y sont liés.";
        } else if (itemType === 'analysis') {
            return "Attention : La suppression de cette analyse supprimera définitivement toutes les données associées (valeurs métiers, scénarios, rapports, etc.).";
        }
        return t('businessValues.confirmations.warningMessage', 'Cette action est irréversible. Toutes les données associées seront définitivement supprimées.');
    };

    // Use React Portal to render modal at document.body level
    // This ensures the modal displays as a fullscreen overlay, breaking free from parent CSS constraints
    return ReactDOM.createPortal(
        <AnimatePresence>
            {isOpen && (
                <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
                    {/* Backdrop */}
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={onClose}
                        className="absolute inset-0 bg-black bg-opacity-50 backdrop-blur-sm"
                    />

                    {/* Modal */}
                    <motion.div
                        initial={{ opacity: 0, scale: 0.95, y: 20 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.95, y: 20 }}
                        transition={{ type: "spring", duration: 0.5 }}
                        className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden"
                    >
                        {/* Header with gradient */}
                        <div className={`px-6 py-4 ${itemType === 'unlinkSupportAsset' ? 'bg-gradient-to-r from-orange-500 to-orange-600' : 'bg-gradient-to-r from-red-500 to-red-600'}`}>
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                    <div className="bg-white bg-opacity-20 p-2 rounded-lg">
                                        <AlertTriangle size={24} className="text-white" />
                                    </div>
                                    <h3 className="text-xl font-bold text-white">
                                        {title || defaultTitle}
                                    </h3>
                                </div>
                                <button
                                    onClick={onClose}
                                    className="text-white hover:bg-white hover:bg-opacity-20 p-1 rounded-lg transition-colors"
                                >
                                    <X size={20} />
                                </button>
                            </div>
                        </div>

                        {/* Content */}
                        <div className="px-6 py-6">
                            <div className="mb-6">
                                <p className="text-gray-700 text-base leading-relaxed">
                                    {message || defaultMessage}
                                </p>
                                {itemName && (
                                    <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                                        <p className="text-sm text-red-800">
                                            <span className="font-semibold">
                                                {itemType === 'businessValue' ? t('businessValues.fields.businessValueName', 'Valeur métier') : 
                                                 itemType === 'securityControl' ? t('securityControls.modal.controlName', 'Contrôle') :
                                                 itemType === 'ecosystemMeasure' ? t('workshop4.operationalScenarios.measureForm.fields.title', 'Mesure') :
                                                 itemType === 'securityRule' ? t('securityFoundation.sections.rule', 'Règle') :
                                                 itemType === 'bulkMeasures' ? t('common.count', 'Nombre') :
                                                 t('businessValues.fields.supportAssetName', 'Bien support')} :
                                            </span>
                                            <span className="ml-2 font-mono">{itemName}</span>
                                        </p>
                                    </div>
                                )}
                            </div>

                            {itemType !== 'unlinkSupportAsset' && (
                                <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-6">
                                    <div className="flex">
                                        <div className="flex-shrink-0">
                                            <AlertTriangle className="h-5 w-5 text-yellow-400" />
                                        </div>
                                        <div className="ml-3">
                                            <p className="text-sm text-yellow-700">
                                                {getCascadeWarningMessage()}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Action Buttons */}
                            <div className="flex gap-3">
                                <button
                                    onClick={onClose}
                                    className="flex-1 px-4 py-2.5 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 font-medium transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-gray-300 focus:ring-offset-2"
                                >
                                    {cancelText || defaultCancelText}
                                </button>
                                <button
                                    onClick={() => {
                                        onConfirm();
                                        onClose();
                                    }}
                                    className={`flex-1 px-4 py-2.5 text-white rounded-lg font-medium transition-all duration-200 shadow-lg hover:shadow-xl focus:outline-none focus:ring-2 focus:ring-offset-2 transform hover:scale-105 ${
                                        itemType === 'unlinkSupportAsset' 
                                        ? 'bg-gradient-to-r from-orange-600 to-orange-700 hover:from-orange-700 hover:to-orange-800 focus:ring-orange-500' 
                                        : 'bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 focus:ring-red-500'
                                    }`}
                                >
                                    {confirmText || defaultConfirmText}
                                </button>
                            </div>
                        </div>
                    </motion.div>
                </div>
            )}
        </AnimatePresence>,
        document.body
    );
};

export default DeleteConfirmationModal;
